/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { BbocTabItems, QuestionTypes } from '../../../core/enums';
import AutoLogoutLayout, { TabContent } from '../common/Layout';
import Helmet from '../../../commonComponents/Helmet';
import TabMenu from '../common/TabMenu';
import { answerSplitCharacter, bbocProfilePageUrl, dashboardPageUrl, otherOptionValue } from '../../../core/constants';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';
import BbocWelcomeSubPage from './BbocWelcomeSubPage';
import BbocScoreSubPage from './BbocScoreSubPage';
import BbocTakeActionSubPage from './BbocTakeActionSubPage';
import overallResultData, { BbocResult, getBbocResultContentByScore } from '../../../core/bbocResultData';
import useAsyncAction from '../../../commonComponents/Hooks';
import {
	createSurvey,
	getClientIsBusinessOwnerAndDirector,
	useQuestionsData,
	useSendSurveyResultEmail,
	useSurveyAnswers,
	useSurveyIsEmailSent,
	useSurveys
} from '../../../core/api';
import {
	setDependentFieldsAndRedirectToQuestionPage,
	getNextQuestionId,
	calculateOverallScore
} from '../../../core/domain';
import ClientAndSurveyResult from '../../../models/ClientAndSurveyResult';
import SurveyAnswer from '../../../models/SurveyAnswer';
import { getSurveyUniqueIdFromQuery, isLoggedIn } from '../../../core/localStorage';
import { QuestionAnswer, SurveyResultEmailDetails } from '../../../models/EmailDetails';
import { Question } from './questions/questionData';
import useClientContextStore from '../../../zustandStore/clientContextStore';

const BbocPage = () => {
	const fetchClientDetails = useClientContextStore((state) => state.fetchClientDetails);
	const clientDetails = useClientContextStore((state) => state.clientDetails);
	const questionsData = useQuestionsData() ?? [];

	const navigate = useNavigateWithAppName();
	const location = useLocation();
	const [currentTab, setCurrentTab] = useState(BbocTabItems.Score);
	const performAsyncAction = useAsyncAction();
	const { signal } = new AbortController();
	const [selectedBbocResultContent, setSelectedBbocResultContent] = useState<BbocResult>(null);
	const [showBbocWelcomeSubPage, setShowBbocWelcomeSubPage] = useState(false);
	const [showBbocScoreSubPage, setShowBbocScoreSubPage] = useState(false);
	const [hasIncompleteSurvey, setHasIncompleteSurvey] = useState(false);

	const resultUrlSurveyUniqueId = getSurveyUniqueIdFromUrl(location); // result page for both public and logged in user
	const [latestAndCompletedSurveyUniqueId, setLatestAndCompletedSurveyUniqueId] =
		useState<string>(resultUrlSurveyUniqueId);
	const [latestSurveyUniqueId, setLatestSurveyUniqueId] = useState<string>();

	const surveys = useSurveys(isLoggedIn());
	// Will get answers for the latest and completed survey if logged in, otherwise for the public bboc current survey
	const surveyAnswers = useSurveyAnswers(latestAndCompletedSurveyUniqueId);
	const inProgressSurveyAnswers = useSurveyAnswers(latestSurveyUniqueId);
	const isSurveyEmailSent = useSurveyIsEmailSent(resultUrlSurveyUniqueId);

	const queryClient = useQueryClient();
	const sendSurveyResultEmailMutation = useSendSurveyResultEmail(queryClient);

	useEffect(() => {
		// Save the score, mark the survey as completed, and send the email
		if (resultUrlSurveyUniqueId && isSurveyEmailSent === false && surveyAnswers?.length > 0) {
			fetchClientDetails(); // To get the latest bboc score updated by useSurveyIsEmailSent
			saveScoreAndSendEmail(surveyAnswers);
		}
	}, [isSurveyEmailSent, surveyAnswers]);

	useEffect(() => {
		if (surveys !== undefined) {
			if (surveys?.length) {
				const latestSurvey = surveys[0];
				setLatestSurveyUniqueId(latestSurvey.surveyUniqueId);
				setHasIncompleteSurvey(!latestSurvey.dateCompleted);

				const latestCompletedSurvey = surveys.find((survey) => survey.dateCompleted);
				if (latestCompletedSurvey) {
					setLatestAndCompletedSurveyUniqueId(latestCompletedSurvey.surveyUniqueId);
				}
			}
		}
	}, [surveys]);

	useEffect(() => {
		const loggedIn = isLoggedIn();

		// Logged-in path must wait until the client store has loaded.
		if (loggedIn && clientDetails === null) return;

		const hasSurveyAnswers = surveyAnswers?.length > 0;
		const hasResultUrl = !!resultUrlSurveyUniqueId;
		const hasScoreData = loggedIn && !!clientDetails?.latestBbocScore;

		if (hasResultUrl || hasSurveyAnswers || hasScoreData) {
			// Result variant — for public users we need answers loaded before we can score.
			if (hasResultUrl && !hasSurveyAnswers && !hasScoreData) return;

			const overallScore = hasScoreData ? clientDetails.latestBbocScore : calculateOverallScore(surveyAnswers);
			const bbocResultContent = getBbocResultContentByScore(overallScore);
			if (bbocResultContent) {
				setSelectedBbocResultContent(bbocResultContent);
				setShowBbocWelcomeSubPage(false);
				setShowBbocScoreSubPage(true);
			}
		} else {
			// No survey data and no score data - show welcome page
			setShowBbocWelcomeSubPage(true);
			setShowBbocScoreSubPage(false);
		}
	}, [clientDetails, surveyAnswers, resultUrlSurveyUniqueId]);

	// TODO: consider to get director email from partner card data for loggedin user
	const saveScoreAndSendEmail = async (savedSurveyAnswers: SurveyAnswer[]) => {
		const overallScore = calculateOverallScore(savedSurveyAnswers);
		const clientDirector = await getClientIsBusinessOwnerAndDirector(resultUrlSurveyUniqueId, signal);

		if (overallScore > 0 && questionsData.length > 0) {
			const persona = getPersona(overallScore, overallResultData);
			const emailDetails: SurveyResultEmailDetails = {
				surveyUniqueId: resultUrlSurveyUniqueId,
				directorEmail: clientDirector.director?.email,
				overallScore,
				persona,
				questionAnswers: getAnswers(savedSurveyAnswers, questionsData)
			};

			// Send email result
			sendSurveyResultEmailMutation.mutate(emailDetails);
		}
	};

	const handleStartNewChallengeClick = async () => {
		if (!isLoggedIn()) {
			navigate(bbocProfilePageUrl);
		} else {
			await performAsyncAction(async () => {
				const clientAndSurvey = await createSurvey();
				setDependentFieldsAndRedirectToQuestionPage(clientAndSurvey, 1, navigate);
			});
		}
	};

	const handleContinueChallengeClick = async () => {
		if (latestSurveyUniqueId) {
			const nextQuestionId = getNextQuestionId(inProgressSurveyAnswers, questionsData);

			const clientAndSurvey: ClientAndSurveyResult = {
				clientUniqueId: clientDetails.clientUniqueId,
				surveyUniqueId: latestSurveyUniqueId,
				isBusinessOwner: clientDetails.isBusinessOwner,
				ageGroup: clientDetails.ageGroup
			};
			setDependentFieldsAndRedirectToQuestionPage(clientAndSurvey, nextQuestionId, navigate);
		}
	};

	const handleTabItemClick = (tabItem: BbocTabItems) => {
		setCurrentTab(tabItem);
	};

	const handleBackButtonClick = () => {
		navigate(dashboardPageUrl);
	};

	const getSurveyButtonClickHandler = () => {
		return hasIncompleteSurvey ? handleContinueChallengeClick : handleStartNewChallengeClick;
	};

	return (
		<AutoLogoutLayout
			showFooter={false}
			headerText="Financial Progress Score"
			onBackButtonClick={isLoggedIn() ? handleBackButtonClick : null}
			scrollResetDependency={currentTab}
		>
			<Helmet>
				<title>Financial Progress Score</title>
			</Helmet>
			{showBbocWelcomeSubPage && (
				<BbocWelcomeSubPage
					buttonText={isLoggedIn() && hasIncompleteSurvey ? 'Continue' : 'Start challenge'}
					onButtonClick={isLoggedIn() ? getSurveyButtonClickHandler() : handleStartNewChallengeClick}
				/>
			)}
			{showBbocScoreSubPage && (
				<>
					<TabMenu enumType={BbocTabItems} selectedTabItem={currentTab} onTabItemClick={handleTabItemClick} />
					<TabContent>
						{currentTab === BbocTabItems.Score && (
							<BbocScoreSubPage
								surveyAnswers={surveyAnswers}
								surveyQuestions={questionsData}
								buttonText={isLoggedIn() && hasIncompleteSurvey ? 'Continue' : 'Retake now'}
								onButtonClick={isLoggedIn() ? getSurveyButtonClickHandler() : null}
								hasIncompleteSurvey={hasIncompleteSurvey}
							/>
						)}
						{currentTab === BbocTabItems.TakeAction && <BbocTakeActionSubPage bboc={selectedBbocResultContent} />}
					</TabContent>
				</>
			)}
		</AutoLogoutLayout>
	);
};

export default BbocPage;

const getSurveyUniqueIdFromUrl = (location: any) => {
	return getSurveyUniqueIdFromQuery(location);
};

const getAnswers = (surveyAnswers: SurveyAnswer[], questions: Question[]): QuestionAnswer[] => {
	return surveyAnswers.map((a) => {
		const question = questions.find((x) => x.uniqueName === a.questionUniqueName);
		const questionTitle = `${question.title} ${question.sliderTitle ?? ''}`.trim();
		// Only need to show section title on top of the first section question, so set to null for others.
		const isFirstSectionQuestion =
			questions.filter((x) => x.section.title === question.section.title)[0].uniqueName === question.uniqueName;
		const sectionTitle = isFirstSectionQuestion ? question.section.title : null;
		const answers = mapAnswerValueToLabelTextArray(a.answer, question);
		return { questionTitle, answers, sectionTitle };
	});
};

const mapAnswerValueToLabelTextArray = (answer: string, questionSpec: Question) => {
	if (questionSpec.type === QuestionTypes.Slider) return [answer]; // Slider saves label text as answer.
	if (questionSpec.type === QuestionTypes.RadioList) {
		const labelText = questionSpec.options.find((o) => o.value === answer)?.label;
		if (labelText) return [labelText];
		if (questionSpec.options.some((o) => o.value === otherOptionValue)) return [answer];
		throw new Error(`Unexpected answer value: ${answer}.`);
	}

	const answerValueArray = answer.split(answerSplitCharacter);
	return answerValueArray.map((x) => {
		const labelText = questionSpec.options.find((o) => o.value === x)?.label;
		if (labelText) return labelText;
		if (questionSpec.options.some((o) => o.value === otherOptionValue)) return x;
		throw new Error(`Unexpected answer value: ${x}.`);
	});
};

const getPersona = (score: number, overallResults: any[]) => {
	const scoreData = overallResults.find((x) => x.min <= score && score <= x.max);
	return `${scoreData.min} - ${scoreData.max} ${scoreData.title}`;
};
